// Service for fetching admin settings (UPI ID, etc.)
import axios from 'axios';

const API_BASE_URL = '/api/settings';

/**
 * Roles that must configure billing UPI (global or per-business) before home dashboard.
 * Matches who can save via POST /admin/upi.
 */
export const ROLES_NEED_DEFAULT_UPI_SETUP = new Set([
  'app_admin',
  'super_admin',
  'business_admin',
  'business_owner',
  'shop_owner',
  'shop_admin',
]);

export const isDefaultUpiConfigured = (upiId) =>
  typeof upiId === 'string' && upiId.trim().length > 0;

// Effective billing UPI for QR (global for admins; per-business for business roles)
export const fetchDefaultUpiId = async () => {
  try {
    const profile = await fetchBillingUpiProfile();
    return profile.upi_id;
  } catch (error) {
    if (error.response?.status === 404) {
      return '';
    }
    console.error('Error fetching UPI ID:', error);
    throw new Error('Failed to load UPI configuration. Please try again.');
  }
};

export const fetchBillingUpiProfile = async () => {
  const empty = { upi_id: '', upi_account_name: '', upi_consent_at: null };
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/upi`);
    if (response.data?.success) {
      const v = response.data.upi_id;
      return {
        upi_id: typeof v === 'string' ? v.trim() : '',
        upi_account_name: String(response.data.upi_account_name || '').trim(),
        upi_consent_at: response.data.upi_consent_at || null,
        consent_text_en: response.data.consent_text_en || '',
        consent_text_te: response.data.consent_text_te || '',
      };
    }
    return empty;
  } catch (error) {
    if (error.response?.status === 404) {
      return empty;
    }
    throw error;
  }
};

/**
 * After authentication, return `/bills/admin-controls` when billing UPI is unset.
 */
export async function resolvePostLoginPath(userRole, getDefaultRoute) {
  const fallback = getDefaultRoute(userRole);
  if (!userRole || !ROLES_NEED_DEFAULT_UPI_SETUP.has(userRole)) {
    return fallback;
  }
  try {
    const upi = await fetchDefaultUpiId();
    if (!isDefaultUpiConfigured(upi)) {
      return '/bills/admin-controls';
    }
  } catch {
    // Network or server error — do not block login on Admin Controls
  }
  return fallback;
}

export const fetchBillOtherTaxes = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL}/admin/bill-other-taxes`);
    if (response.data?.success) {
      return response.data;
    }
    return { tax1: { enabled: false, label: 'Service Charge', percent: 0 }, tax2: { enabled: false, label: 'Municipal Tax', percent: 0 } };
  } catch (error) {
    if (error.response?.status === 404) {
      return { tax1: { enabled: false, label: 'Service Charge', percent: 0 }, tax2: { enabled: false, label: 'Municipal Tax', percent: 0 } };
    }
    console.error('Error fetching bill other taxes:', error);
    return { tax1: { enabled: false, label: 'Service Charge', percent: 0 }, tax2: { enabled: false, label: 'Municipal Tax', percent: 0 } };
  }
};

export const saveBillOtherTaxes = async (payload) => {
  const response = await axios.post(`${API_BASE_URL}/admin/bill-other-taxes`, payload);
  if (response.data?.success) {
    return response.data;
  }
  throw new Error(response.data?.message || 'Failed to save bill other taxes');
};

// Save billing UPI (global or business row depending on role).
// Changing UPI requires a 6-digit OTP sent to the registered mobile.
export const sendUpiUpdateOtp = async () => {
  const response = await axios.post(`${API_BASE_URL}/admin/upi/send-otp`);
  if (response.data?.success) {
    return response.data;
  }
  throw new Error(response.data?.message || 'Failed to prepare UPI OTP');
};

export const saveDefaultUpiId = async (upiId, otp, extras = {}) => {
  try {
    const response = await axios.post(`${API_BASE_URL}/admin/upi`, {
      upi_id: upiId,
      ...(otp ? { otp } : {}),
      ...(extras.msg91_access_token
        ? { msg91_access_token: extras.msg91_access_token }
        : {}),
      ...(extras.msg91_request_user_id
        ? { msg91_request_user_id: extras.msg91_request_user_id }
        : {}),
      ...(extras.otp_phone ? { otp_phone: extras.otp_phone } : {}),
      ...(extras.upi_account_name
        ? { upi_account_name: extras.upi_account_name }
        : {}),
      ...(extras.upi_consent ? { upi_consent: true } : {}),
    });
    if (response.data.success) {
      return response.data.data;
    } else {
      throw new Error(response.data.message || 'Failed to save UPI ID');
    }
  } catch (error) {
    console.error('Error saving UPI ID:', error);
    throw error;
  }
};
